import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { ArrowUp, Square } from "lucide-react";
import * as endPoints from "../../constants/v2/endPoints";
import {
  resolveAuthHeaders,
  getBlsEnvHeader,
} from "../../services/v2/apiService";
import { parseSseBuffer } from "../../helpers/sseParser";
import {
  groupMarkdownBlocks,
  listingRefUrl,
  photoRefUrl,
  textForAnnouncement,
} from "../../helpers/simpleMarkdown";
import { resolveCurrentPageRecord } from "../../helpers/currentPageContext";
import { ListingCardList } from "./ListingCard";
import { applyActivityEvent } from "../../helpers/chatActivity";
import { applyStepEvent, STEP_FALLBACK_LABEL } from "../../helpers/copilotSteps";
import {
  applyFieldValue,
  normalizeResult,
  REF_TYPE_BY_RESULT_KIND,
} from "../../helpers/copilotResult";
import { normalizeInsights } from "../../helpers/copilotInsights";
import CopilotWorkPanel from "../CopilotWorkPanel";
import CopilotResultCard from "../CopilotResultCard";
import CopilotInsightsCard from "../CopilotInsightsCard";
import {
  saveChatFeedback,
  updateDraftField,
} from "@/services/copilotService";
import {
  buildOutgoingMessages,
  deriveConversationTitle,
} from "../../helpers/chatHistory";
import { createFrameCoalescer } from "../../helpers/frameCoalescer";
import { createPendingActionGuard } from "../../helpers/pendingActions";
import {
  GENERIC_CHAT_ERROR_MESSAGE,
  messageForFailedResponse,
} from "../../helpers/helpChatErrors";
import { applyStreamFailure } from "../../helpers/streamFailureMessage";
import { retryMessageId } from "../../helpers/retryMessageId";
import { getLocalStorage } from "../../helpers/common";
import { randomUUID } from "../../helpers/randomId";
import { USER_INFO_KEY } from "../../constants/common";
import "./HelpChatPanel.scss";
import { getAppPathname } from "@/helpers/basePath";

// Framed as jobs, not questions. The gitcha-mcp server exposes write tools
// (create_want_listing, create_inventory, create_client, find_matches,
// get_active_buyer_report, ...), so the assistant can carry these out - the
// old "How do I ...?" prompts only ever invited it to explain the UI back.
// The old four chips all assumed the agent already had something: a newest
// Want-Listing to match, a property to get insights on. A new agent has
// neither, so half the starting points were dead ends on day one. These are
// worked examples instead - each one is a complete sentence an agent could
// send as-is, and each one lands somewhere real without needing any prior
// record to exist.
//
// The first example does assume a published Want-Listing. That is deliberate:
// Gitcha is not a property search site, so the lead example must not read like
// one. An agent with no listing yet is not a dead end - the assistant explains
// that there is nothing to match yet and offers to draft one.
//
// Every example describes a PROPERTY, never a person. These are the first
// sentences an agent sees, so they teach the shape of a request: an earlier
// draft said "a buyer relocating this spring", which invites an agent to
// volunteer why someone is moving. Wants, needs and criteria only.
const INSPIRATIONS = [
  "Show me matches for my newest Want-Listing",
  "Store 123 Main Street as a prospect's property and find buyer matches",
  "How many Buyer Want-Listings with cash buyers match 79424 in the next 3 to 4 months?",
  "Draft a Want-Listing for my buyer: 4 bed, an acre or more, up to $650,000",
];

// Slow enough to finish reading one before the next arrives.
const INSPIRATION_MS = 5200;


/**
 * A rotating example inside the empty composer, in place of a static
 * placeholder.
 *
 * Decorative and not interactive: it sits under the textarea with pointer
 * events off, so a click lands in the field and typing hides it. It exists to
 * show an agent the SHAPE of a request that works, not to be pressed.
 *
 * It stops entirely for a visitor who has asked their system to reduce
 * motion, who sees the first example and nothing moving. The textarea keeps
 * its own aria-label, so a screen reader is never read this rotation.
 */
const InspirationPlaceholder = ({ hidden }) => {
  const [index, setIndex] = useState(0);
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (hidden || reduceMotion) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % INSPIRATIONS.length),
      INSPIRATION_MS,
    );
    return () => clearInterval(id);
  }, [hidden, reduceMotion]);

  if (hidden) return null;
  const current = INSPIRATIONS[index];
  return (
    <span className="chat-inspiration" aria-hidden="true">
      <span className="chat-inspiration__line" key={current}>
        {current}
      </span>
    </span>
  );
};
InspirationPlaceholder.displayName = "InspirationPlaceholder";

// First name only, and only when we have one - "Hey there" is friendlier than
// a greeting addressed to an empty string.
const agentFirstName = () => {
  const name = getLocalStorage(USER_INFO_KEY)?.userInfo?.firstName;
  return typeof name === "string" && name.trim() ? name.trim() : null;
};

// No self-introduction and no capability menu. The good assistants open a
// conversation rather than announcing themselves and listing what they can do.
const greetingText = () => {
  const name = agentFirstName();
  return name
    ? `Hey ${name} - what are you working on?`
    : "Hey - what are you working on?";
};

// `isGreeting` marks this as client-fabricated, never something the model
// produced - buildOutgoingMessages/send strip it before it ever reaches the
// API, so the model isn't shown a greeting it never said (a bug caught in
// review).
const buildGreeting = () => ({
  role: "assistant",
  content: greetingText(),
  isGreeting: true,
});

// listingRefUrl/photoRefUrl returning null (an id-less/malformed marker, an
// unrecognized type, or - for photos specifically - a URL that fails the
// host allowlist) renders the label as inert plain text rather than a
// broken link or an unchecked <img src> - matches simpleMarkdown.js's own
// "degrade to plain text, never a broken/unsafe reference" design.
// Assistant turns render as a document (avatar + name + timestamp, prose,
// then an action row) rather than as chat bubbles - only the user's own turn
// keeps a bubble. Matches how product assistants present replies: the reply
// is content to read, not a message in a thread.
const formatTime = (ts) => {
  const d = ts ? new Date(ts) : new Date();
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const copyText = async (text) => {
  // navigator.clipboard needs a secure context (the same constraint that
  // broke crypto.randomUUID on http tenant subdomains), so fall back to
  // execCommand rather than silently failing on blc.lvh.me.
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    /* fall through to the legacy path */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch (_e) {
    return false;
  }
};

const renderSegments = (segments) =>
  segments.map((seg, i) => {
    if (seg.type === "bold") return <strong key={i}>{seg.value}</strong>;
    if (seg.type === "code")
      return (
        <code key={i} className="chat-inline-code">
          {seg.value}
        </code>
      );
    if (seg.type === "listing-ref") {
      const url = listingRefUrl(seg);
      return url ? (
        <Link key={i} to={url} className="chat-listing-ref">
          {seg.label}
        </Link>
      ) : (
        seg.label
      );
    }
    if (seg.type === "photo-ref") {
      const url = photoRefUrl(seg);
      return url ? (
        <img
          key={i}
          src={url}
          alt={seg.label}
          className="chat-photo-ref"
          loading="lazy"
        />
      ) : (
        seg.label
      );
    }
    return seg.value;
  });

// `resultCard` is the listing the turn's own result card is already showing
// in full. Any inline [[card:...]] the model wrote for that same listing is a
// second, smaller copy of it directly above the real one - two cards, one
// draft. Invisible until result cards were persisted, because the inline
// marker was the only one that survived a reload; once both came back the
// duplication showed up on every draft.
//
// Matched by refType+id, not id alone - refIds are only unique within a
// refType (ListingCard.js), so an inventory_draft with the same numeric id
// as an unrelated listing/mls card would otherwise hide it too (a bug
// caught in review).
const MarkdownContent = ({ text, streaming, onSelectCard, onChipSend, openInPlace, disableLinks, resultCard }) => {
  // Re-parsing the whole accumulated reply is O(current length); without
  // this, it ran on every coalesced animation frame during a stream, making
  // a single reply's total render cost O(n^2) in its length (a bug caught
  // in review). `streaming` is a dependency too - groupMarkdownBlocks uses
  // it for more than the trailing-marker handling (see its own docstring).
  const blocks = useMemo(
    () => groupMarkdownBlocks(text, { streaming }),
    [text, streaming],
  );
  return (
    <>
    {blocks.map((block, i) => {
      if (block.type === "bullet-group") {
        return (
          <ul key={i}>
            {block.items.map((segs, j) => (
              <li key={j}>{renderSegments(segs)}</li>
            ))}
          </ul>
        );
      }
      if (block.type === "ordered-group") {
        return (
          <ol key={i}>
            {block.items.map((segs, j) => (
              <li key={j}>{renderSegments(segs)}</li>
            ))}
          </ol>
        );
      }
      if (block.type === "heading") {
        // Chat replies live inside the page's heading outline, so the
        // model's h1/h2 are demoted to h4-h6 rather than competing with
        // the page title. Level is clamped by HEADING_PATTERN (1-6).
        const Tag = `h${Math.min(6, block.level + 3)}`;
        return (
          <Tag key={i} className="chat-heading">
            {renderSegments(block.segments)}
          </Tag>
        );
      }
      if (block.type === "card-group") {
        // A choice group is never deduped: those cards are the question
        // being asked, not a summary of something already saved.
        const resultRefType =
          resultCard?.id != null
            ? REF_TYPE_BY_RESULT_KIND[resultCard.kind]
            : undefined;
        const items = block.selectable
          ? block.items
          : block.items.filter(
              (card) =>
                !(
                  resultRefType &&
                  card.refType === resultRefType &&
                  String(card.refId) === String(resultCard.id)
                ),
            );
        if (!items.length) return null;
        return (
          <ListingCardList
            key={i}
            items={items}
            selectable={block.selectable}
            onSelect={onSelectCard}
            openInPlace={openInPlace}
            disableLinks={disableLinks}
          />
        );
      }
      if (block.type === "chip-group") {
        return (
          <div key={i} className="chat-suggestions">
            {block.items.map((text, j) => (
              <button
                key={j}
                type="button"
                className="suggestion-chip"
                onClick={() => onChipSend(text)}
                disabled={disableLinks}
              >
                {text}
              </button>
            ))}
          </div>
        );
      }
      return <p key={i}>{renderSegments(block.segments)}</p>;
    })}
    </>
  );
};

// Memoized so a streamed token only re-renders (and re-parses markdown for)
// the ONE turn that's actually changing. `send` below always updates
// `messages` via `[...prev]` + replacing just the last element - every other
// message keeps the same object reference across renders, so React.memo's
// default shallow comparison skips re-rendering them for free.
//
// `isStreaming` is only ever true for the last turn, so it does not defeat
// the memo for the earlier ones.
// `onRetry`/`index` (not a bound closure per turn) so the callback prop stays
// referentially stable across renders - a fresh arrow function per turn on
// every state update would defeat React.memo for every turn, not just the
// one that changed, which is the exact re-render cost this component exists
// to avoid.
const AssistantTurn = React.memo(({
  message,
  isStreaming,
  onRetry,
  onRegenerate,
  onSelectCard,
  onChipSend,
  openInPlace,
  disableLinks,
  onRate,
  onEditResultField,
  onResultAction,
  canRegenerate,
  index,
}) => {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(copyTimeoutRef.current), []);

  const onCopy = async () => {
    const ok = await copyText(message.content || "");
    if (ok) {
      clearTimeout(copyTimeoutRef.current);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 1600);
    }
  };

  const steps = message.activitySteps;
  const hasActivity = Boolean(steps && steps.length > 0);
  // A streaming turn shows the work panel from the moment it starts, not from
  // its first tool call - `workStartedAt` is set when the turn is created, so
  // the panel can run its clock through the wait before any step exists,
  // which is the stretch that used to show a static label and no elapsed time
  // at all. A turn that never calls a tool hands off to its own streaming
  // text once tokens start arriving, rather than running a second indicator
  // alongside it. A settled turn shows the panel only when there is real work
  // to summarize.
  const showWork =
    hasActivity ||
    Boolean(isStreaming && message.workStartedAt && !message.content);
  const rating = message.feedback || null;
  const canRate = Boolean(message.serverMessageId);

  return (
    <div className="chat-turn chat-turn--assistant">
      <div className="turn-meta">
        <span
          className={`turn-avatar${isStreaming ? " turn-avatar--busy" : ""}`}
          aria-hidden="true"
        >
          &#10022;
        </span>
        <span className="turn-name">Genie</span>
        <span className="turn-time">{formatTime(message.ts)}</span>
      </div>
      <div className={`turn-body${isStreaming ? " turn-body--streaming" : ""}`}>
        {showWork && (
          <CopilotWorkPanel
            steps={steps}
            running={isStreaming}
            startedAt={message.workStartedAt}
            totalMs={message.workMs}
            serverElapsedMs={message.workElapsedMs}
          />
        )}
        {/* The card first, then the sentences about it. The draft IS the
            answer: leading with three lines of prose makes an agent read
            them to find out whether anything was actually saved, and the
            closing question only makes sense once they can see what it is
            asking about. */}
        {message.result && (
          <CopilotResultCard
            result={message.result}
            onEditField={(key, value) => onEditResultField(index, key, value)}
            onAction={(action) => onResultAction(index, action)}
            openInPlace={openInPlace}
            disableLinks={disableLinks}
          />
        )}
        {message.content ? (
          <>
            <MarkdownContent
              text={message.content}
              streaming={isStreaming}
              resultCard={message.result}
              onSelectCard={onSelectCard}
              onChipSend={onChipSend}
              openInPlace={openInPlace}
              disableLinks={disableLinks}
            />
            {isStreaming && (
              <span className="turn-streaming-indicator" role="status">
                <span className="turn-streaming-dot" aria-hidden="true" />
                Thinking&hellip;
              </span>
            )}
          </>
        ) : (
          !showWork && (
            // Fallback for a turn with no content, no work and no clock to
            // run (a stopped or unpersisted turn rehydrated from history) -
            // a live turn takes the work panel's running state above, which
            // carries the elapsed time and the copy that escalates with it.
            // The shimmer is CSS-only (a gradient swept across the text).
            <span className="chat-thinking" role="status">
              {STEP_FALLBACK_LABEL}
            </span>
          )
        )}
        {message.insights && (
          <CopilotInsightsCard
            insights={message.insights}
            onChipSend={onChipSend}
            openInPlace={openInPlace}
            disableLinks={disableLinks}
          />
        )}
      </div>
      {message.truncated && !isStreaming && (
        <div className="turn-truncated" role="status">
          <span>
            {message.truncatedMessage || "This reply was cut off."}
          </span>
          <button type="button" onClick={() => onRetry(index)}>
            Retry
          </button>
        </div>
      )}
      {message.content && !isStreaming && (
        <div className="turn-actions">
          <button type="button" onClick={onCopy} aria-label="Copy reply">
            {copied ? "Copied" : "Copy"}
          </button>
          {canRegenerate && (
            <button
              type="button"
              onClick={() => onRegenerate(index)}
              aria-label="Regenerate this reply"
            >
              Regenerate
            </button>
          )}
          {canRate && (
            <>
              <button
                type="button"
                className={rating === "up" ? "is-active" : ""}
                onClick={() => onRate(index, rating === "up" ? null : "up")}
                aria-label="Good response"
                aria-pressed={rating === "up"}
              >
                Helpful
              </button>
              <button
                type="button"
                className={rating === "down" ? "is-active" : ""}
                onClick={() => onRate(index, rating === "down" ? null : "down")}
                aria-label="Bad response"
                aria-pressed={rating === "down"}
              >
                Not helpful
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
});
AssistantTurn.displayName = "AssistantTurn";

const UserTurn = React.memo(({ message, index, onEditAndResend, canEdit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const begin = () => {
    setDraft(message.content || "");
    setEditing(true);
  };

  const submit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    if (trimmed && trimmed !== message.content) onEditAndResend(index, trimmed);
  };

  const onKeyDown = (event) => {
    // isComposing: true while an IME candidate (CJK input, etc.) is still
    // being chosen - Enter there confirms the candidate, it isn't the
    // user asking to submit (a bug caught in review).
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
    if (event.key === "Escape") {
      event.stopPropagation();
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="chat-turn chat-turn--user chat-turn--editing">
        <textarea
          className="user-edit-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          aria-label="Edit your message"
          autoFocus
        />
        <div className="user-edit-actions">
          <button type="button" onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="is-primary"
            onClick={submit}
            disabled={!draft.trim()}
          >
            Send
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-turn chat-turn--user">
      <div className="user-bubble">{message.content}</div>
      {canEdit && (
        <button
          type="button"
          className="user-edit-btn"
          onClick={begin}
          aria-label="Edit and resend this message"
        >
          Edit
        </button>
      )}
    </div>
  );
});
UserTurn.displayName = "UserTurn";

const isLoggedIn = () =>
  Boolean(getLocalStorage(USER_INFO_KEY)?.token?.accessToken);

// `layout`/`initialMessages`/`conversationId`/`onConversationStarted` exist
// for the full-page chat (src/pages/Chat/ChatPage.js) without touching the
// default behaviour SupportTicketModal relies on:
//  - layout="page" (default "panel"): swaps the empty-state markup for a
//    centered "How can I help you...?" prompt instead of a greeting bubble.
//    The message-thread/composer/SSE logic below is unchanged either way.
//  - initialMessages: seeds the thread when opening a saved conversation
//    from the history sidebar. The parent remounts this component (via
//    `key`) when switching conversations, so this only needs to matter on
//    first mount.
//  - conversationId: the conversation being resumed (undefined for a brand
//    new chat). Sent with every outgoing message so the server groups all
//    turns under the same conversation instead of minting a new one per
//    message.
//  - onConversationStarted: fired once a brand-new conversation's id comes
//    back from the server (the `done` event of its first turn), so the
//    history sidebar can refresh and mark it active.
const HelpChatPanel = ({
  onEscalateToTicket,
  layout = "panel",
  initialMessages,
  conversationId,
  onConversationStarted,
  onConversationDraft,
  onConversationDraftFailed,
}) => {
  const [messages, setMessages] = useState(() => {
    const initial =
      initialMessages && initialMessages.length
        ? initialMessages
        : [buildGreeting()];
    // A stable id per turn, not the array index - AssistantTurn carries local
    // state (copied/rating) keyed by React's `key`, and stop() below removes
    // the last element, which shifts every index after it. An index key
    // would attach that local state to the wrong turn the moment the list
    // shrinks or reorders (a bug caught in review). Loaded history has no id
    // from the API (see chatHistoryService.js's adaptMessage), so it's
    // assigned here, once, on load.
    return initial.map((m) => (m.id ? m : { ...m, id: randomUUID() }));
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  // The main thread view's root element - Escape-to-stop below is bound
  // here, not `document`, so it doesn't also trigger react-bootstrap
  // Modal's own document-level Escape handling in the docked-panel case.
  const panelRef = useRef(null);
  const abortRef = useRef(null);
  const wasNearBottomRef = useRef(true);
  // Mutable, not state: updating it must never trigger a re-render (the
  // panel intentionally stays mounted across a new conversation getting its
  // first id - see ChatPage.js's conversationStarted handling), and every
  // `send` needs the latest value synchronously, not after a render cycle.
  const conversationIdRef = useRef(conversationId);
  // Batches the `chunk` events a single network read() can carry into at
  // most one setMessages (one re-render of the whole thread) per animation
  // frame, instead of one per event. Lazily built once via the ref itself
  // (not useMemo/useState) since it only wraps the stable setMessages
  // setter - see createFrameCoalescer's own docstring.
  const coalescerRef = useRef(null);
  if (!coalescerRef.current) {
    coalescerRef.current = createFrameCoalescer((text) => {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: text };
        return copy;
      });
    });
  }
  // The in-progress turn's own activity trail (see applyActivityEvent).
  // Reset to [] at the top of every `send()` - each user message starts a
  // brand-new assistant turn with its own fresh steps, never carrying a
  // prior turn's (already-rendered, already-settled) steps forward. Mutable
  // like conversationIdRef/coalescerRef above: `send` needs the latest value
  // synchronously as `activity` events arrive, not after a render cycle.
  const activityRef = useRef([]);
  const sawStepRef = useRef(false);
  const workStartedAtRef = useRef(null);
  // The turn's accumulated text, mirrored outside the coalescer's own
  // committed state - requestAnimationFrame (which the coalescer commits
  // through) never fires in a hidden/backgrounded tab, so `messages` can
  // still read as empty content even once the model has streamed a real,
  // mostly-complete answer. stop() below reads this to recover that text
  // rather than losing it (a bug caught in review).
  const assistantTextRef = useRef("");
  // Set synchronously at the very start of `send`, before any `await` -
  // `streaming` is a closure read that two calls dispatched in the same
  // tick can both pass before either commits its `setStreaming(true)`
  // (the same race createPendingActionGuard below exists to close for
  // result-card actions). This ref closes it for `send` itself, so a
  // double-click/double-Enter can't fire two concurrent streams that both
  // write into the same last message (a bug caught in review).
  const sendingRef = useRef(false);
  // Held only so the outer `finally` below can release the reader's lock
  // structurally, regardless of which path got there - every existing exit
  // already errors the stream via controller.abort() or drains it to
  // `done`, so this is defensive against a future early return between
  // acquiring the reader and reaching that point, not a fix for a live leak.
  const readerRef = useRef(null);
  // A stream that gets no bytes for a while, or simply runs too long, would
  // otherwise hang "Working on it" forever with no way out but Escape - see
  // the watchdog set up in `send` below. `streamTimedOutRef` distinguishes
  // that abort from a deliberate Stop/unmount so the catch block below
  // shows an error for the former and stays silent for the latter.
  const streamTimedOutRef = useRef(false);

  // Cancel any in-flight request when the panel unmounts (modal close, or
  // switching to the ticket tab) - without this, an abandoned conversation
  // keeps streaming (and billing) to completion on the server with no one
  // reading the output. Also drop any frame the coalescer above has queued,
  // so a stray setMessages never fires after the component is gone.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      coalescerRef.current.cancel();
    };
  }, []);

  // useLayoutEffect, not useEffect: this must run synchronously after the
  // DOM commit but before the browser paints. A layout-shrinking commit -
  // CopilotWorkPanel collapsing its step list once `running` flips false,
  // or the `workMs` commit right after it in `send`'s `finally` - already
  // has the shorter DOM in place by the time any effect runs; the
  // difference is whether the user's eye catches a frame of the old
  // scrollTop against the new (shorter) content before the pin lands.
  // useEffect fires after paint, so that mismatched frame is visible and
  // the next paint's snap reads as a jump. useLayoutEffect corrects it
  // before that first paint, so only the settled position is ever shown.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && wasNearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, streaming]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    wasNearBottomRef.current = distanceFromBottom < 80;
  };

  const send = async (text, { replaceFromIndex, messageId } = {}) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || streaming || sendingRef.current) return;
    sendingRef.current = true;

    const history =
      typeof replaceFromIndex === "number"
        ? messages.slice(0, replaceFromIndex)
        : messages;
    // `messageId` lets a caller resend the SAME turn under the same
    // idempotency key (see retryTurnRef below) - only minted fresh here
    // when none is supplied, i.e. for a genuinely new turn.
    const turnMessageId = messageId || randomUUID();
    // A brand-new conversation gets its id here, before the request goes out,
    // so the URL is right for the whole of the reply rather than only after
    // it finishes. onConversationStarted is what puts it in the address bar,
    // and it is what Back comes home to.
    if (!conversationIdRef.current) {
      conversationIdRef.current = randomUUID();
      onConversationStarted && onConversationStarted(conversationIdRef.current);
    }
    const workStartedAt = Date.now();
    const nextMessages = [
      ...history,
      { id: randomUUID(), role: "user", content: trimmed },
      {
        id: randomUUID(),
        role: "assistant",
        content: "",
        serverMessageId: turnMessageId,
        // On the turn from the moment it is created, not from its first
        // `step` event - the first tool call is 1.3 to 5.7 seconds out, and
        // attaching this only in the `step` branch below meant the whole
        // stretch before it showed a static label with no elapsed time, and
        // a turn that never called a tool never showed a duration at all.
        workStartedAt,
      },
    ];
    setMessages(nextMessages);
    setInput("");
    setStreaming(true);
    wasNearBottomRef.current = true;
    activityRef.current = [];
    sawStepRef.current = false;
    workStartedAtRef.current = workStartedAt;
    assistantTextRef.current = "";
    streamTimedOutRef.current = false;
    readerRef.current = null;

    if (!conversationIdRef.current && onConversationDraft) {
      onConversationDraft(deriveConversationTitle(nextMessages));
    }

    const controller = new AbortController();
    abortRef.current = controller;
    // Declared here, not inside the try block below - the catch block reads
    // it too (to preserve partial content on failure), and `let`/`const`
    // inside `try { }` is NOT visible inside the following `catch { }`;
    // they're separate blocks. Referencing it from catch while it was
    // declared inside try threw a ReferenceError on every single error
    // path - network failure, a non-ok response, an SSE `error` event, the
    // empty-stream check - silently discarding the very partial-content
    // preservation this catch block exists to do, on every one of them (a
    // bug caught in review).
    let assistantText = "";
    // Also declared here rather than inside the try block, for the same
    // try/catch/finally-are-separate-blocks reason as assistantText above -
    // clearWatchdogs() below is called from `finally`, which can't see a
    // `const` declared inside `try` either.
    const IDLE_TIMEOUT_MS = 60000;
    const OVERALL_TIMEOUT_MS = 5 * 60 * 1000;
    const watchdogsRef = { idle: null, overall: null };
    const onWatchdogFire = () => {
      streamTimedOutRef.current = true;
      controller.abort();
    };
    const resetIdleWatchdog = () => {
      clearTimeout(watchdogsRef.idle);
      watchdogsRef.idle = setTimeout(onWatchdogFire, IDLE_TIMEOUT_MS);
    };
    const clearWatchdogs = () => {
      clearTimeout(watchdogsRef.idle);
      clearTimeout(watchdogsRef.overall);
    };

    try {
      const authHeaders = await resolveAuthHeaders(null, null);
      // resolveAuthHeaders resolves to undefined (not a rejected promise)
      // on every "session actually expired" branch inside it (a concurrent
      // clearSession() mid-refresh, a decode failure, ...) - spreading that
      // into the fetch below silently sends the request with no
      // Authorization header at all, and the resulting 401 reads as a
      // generic "something went wrong" with no hint that the fix is
      // "sign in again" (a bug caught in review).
      if (!authHeaders || !(authHeaders.Authorization || authHeaders.header)) {
        const err = new Error("Not authenticated");
        err.displayMessage =
          "Your session has expired. Please sign in again.";
        throw err;
      }
      const apiPayload = {
        // Trimmed to the server's own effective history window
        // (MAX_OUTGOING_MESSAGES) - a saved conversation's history only
        // ever grows, and the API hard-rejects any request over 40
        // messages, so sending the full untrimmed thread eventually
        // breaks a long conversation permanently (a bug caught in review).
        messages: buildOutgoingMessages(nextMessages.slice(0, -1)),
        // pathname only, not the query string - a query string can carry
        // sensitive values (tokens, emails) depending on the page. Kept
        // alongside currentRecord below (not replaced by it) for any
        // future server-side use that needs the raw path rather than a
        // parsed record - currentRecord only ever covers the four known
        // detail-page routes. "/r/:token" is excluded outright even as a
        // bare path - it's a bearer-equivalent public share link (see
        // ActiveBuyerReport's public route), not just a page name, and has
        // no business leaving the browser in a chat payload (a bug caught
        // in review).
        currentPage:
          typeof window !== "undefined" &&
          !/^\/r\//.test(getAppPathname())
            ? getAppPathname()
            : null,
        // {kind, id} for the want-listing/inventory/MLS record the agent is
        // currently looking at, or undefined when the current page isn't
        // one of those (see resolveCurrentPageRecord) - so "get demand
        // insights for this property" can resolve "this" without the agent
        // retyping the address. Still just a data field on the request: the
        // gitcha-mcp side has to read and trust it deliberately, same as
        // any other client-supplied context here.
        currentRecord:
          typeof window !== "undefined"
            ? resolveCurrentPageRecord(getAppPathname()) || undefined
            : undefined,
        // Minted here for a brand-new conversation rather than waiting for
        // the server to mint one, because the id is what the URL is made of
        // and the URL is what Back returns to. The server accepts a
        // client-supplied uid (chatHistory.js's isValidClientUid) and only
        // mints its own when one is absent.
        //
        // Waiting cost the agent their place: the id used to arrive on
        // `done`, so for the whole of a streaming reply the page was still
        // at /chat. A draft card appears mid-stream, and clicking through to
        // it and pressing Back landed on the empty chat root instead of the
        // conversation they were in. Reported live.
        conversationId: conversationIdRef.current,
        // Fresh per message (not per conversation) - the server's dedupe
        // guard is keyed on this, so a retry/reconnect of the *same* turn
        // must resend the *same* messageId, not mint a new one. retryTurnRef
        // below passes the failed turn's own serverMessageId back in here
        // for exactly that reason (a bug caught in review: Retry/Regenerate
        // previously minted a fresh id every time, so a truncated stream
        // that had already run a tool call re-ran it on retry).
        messageId: turnMessageId,
      };
      // No bytes at all for a while (an upstream hang after a 200) or the
      // turn simply running too long would otherwise leave "Working on it"
      // showing forever with no way out but Escape (a bug caught in
      // review). Reset on every read() below; either firing aborts the
      // fetch and is distinguished from a deliberate Stop via
      // streamTimedOutRef in the catch block.
      watchdogsRef.overall = setTimeout(onWatchdogFire, OVERALL_TIMEOUT_MS);
      resetIdleWatchdog();

      const response = await fetch(endPoints.HELP_CHAT_STREAM, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-key": process.env.REACT_APP_REST_API_KEY,
          ...authHeaders,
          ...getBlsEnvHeader(),
        },
        body: JSON.stringify(apiPayload),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        const message = await messageForFailedResponse(response);
        // Only messageForFailedResponse's own 429 branch actually reads
        // the body - every other status left it open, holding the
        // connection until GC (a bug caught in review).
        response.body?.cancel().catch(() => {});
        const err = new Error(message);
        // Explicit marker: this message is server-authored and safe to
        // show as-is (e.g. the rate limiter's own text), unlike an
        // arbitrary thrown/network error - see the catch block below.
        err.displayMessage = message;
        throw err;
      }
      // An SSO interstitial, a CDN error page, or any other 200 that
      // isn't actually the stream would otherwise parse into zero events
      // and fail indistinguishably from a model failure at the
      // `!assistantText` check below (a bug caught in review).
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("text/event-stream")) {
        const err = new Error("Chat stream returned an unexpected response");
        err.displayMessage = GENERIC_CHAT_ERROR_MESSAGE;
        throw err;
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = "";
      // Captured, not thrown immediately, from inside processEvents - see
      // the `error` branch below for why.
      let streamError = null;

      // A block with no trailing "\n\n" never surfaces from parseSseBuffer as
      // an event (it's held back as `remainder`, correctly, in case more
      // bytes are still coming) - so the final flush after the read loop
      // ends appends "\n\n" itself to force-close whatever's left in the
      // buffer. A malformed/truncated trailing block is still silently
      // skipped by parseSseBuffer (that's its own designed behavior, not a
      // bug), so this alone does not make the failure loud - see the
      // `!assistantText` check right after the loop below for what does.
      const processEvents = (chunk) => {
        buffer += chunk;
        const { events, remainder } = parseSseBuffer(buffer);
        buffer = remainder;
        for (const { event, data } of events) {
          if (event === "chunk" && typeof data.text === "string") {
            assistantText += data.text;
            assistantTextRef.current = assistantText;
            // Coalesced (see coalescerRef above) rather than calling
            // setMessages directly - a single read() below can carry many
            // chunk events, and re-rendering the whole thread once per
            // event (instead of once per animation frame) was the actual
            // cost here, not the state update itself.
            coalescerRef.current.schedule(assistantText);
          } else if (event === "step") {
            sawStepRef.current = true;
            activityRef.current = applyStepEvent(activityRef.current, data);
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                activitySteps: activityRef.current,
                workStartedAt: workStartedAtRef.current,
              };
              return copy;
            });
          } else if (event === "heartbeat") {
            // The agent service emits this immediately and then every ten
            // seconds, carrying its own measurement of how long the turn has
            // been running. Kept as a floor under the panel's own ticking
            // clock (see CopilotWorkPanel's useElapsed) rather than replacing
            // it: the local tick is the smooth one, but it drifts and gets
            // throttled in a backgrounded tab, and this number does not.
            // Ignored rather than trusted blindly - it arrives as upstream
            // JSON, so a missing/garbage value must not turn the clock into
            // NaN.
            const elapsedMs = data.elapsedMs;
            if (
              typeof elapsedMs === "number" &&
              Number.isFinite(elapsedMs) &&
              elapsedMs >= 0
            ) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  workElapsedMs: elapsedMs,
                };
                return copy;
              });
            }
          } else if (event === "result") {
            const result = normalizeResult(data);
            if (result) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], result };
                return copy;
              });
            }
          } else if (event === "insights") {
            // Independent of `result`: a turn can carry a draft card, an
            // insights card, both, or neither, since they come from
            // different tools. Last one wins on a refresh, same convention
            // as `result`.
            const insights = normalizeInsights(data);
            if (insights) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], insights };
                return copy;
              });
            }
          } else if (event === "activity" && !sawStepRef.current) {
            // `step` is the richer, structured trail (CopilotWorkPanel
            // renders it) - once a turn has emitted even one, `step`
            // supersedes `activity` for the rest of that turn rather than
            // the two being merged. Deliberate, not a bug: this is per-turn
            // (sawStepRef resets at the top of every send()), so a
            // different turn that only ever emits `activity` still shows
            // it.
            // Non-terminal progress update (a tool starting/finishing) - far
            // less frequent than `chunk`, so committed directly rather than
            // through the frame coalescer above. Written with the same
            // `{...last, field: value}` merge shape the coalescer's own
            // commit uses, so an activity update and a coalesced content
            // update can never clobber each other regardless of which
            // lands first.
            activityRef.current = applyActivityEvent(activityRef.current, data);
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                activitySteps: activityRef.current,
              };
              return copy;
            });
          } else if (event === "done") {
            // The server always echoes the conversationId it persisted
            // under (minting one itself on a brand-new conversation's
            // first turn) - capture it so every subsequent turn in this
            // session reuses the same id, and tell the parent once, the
            // first time a new conversation gets a real id, so the
            // sidebar can pick it up without racing the server's own
            // persistence.
            // The id is minted before the request now (see send), so this
            // is a confirmation rather than a discovery. It still adopts the
            // server's value if they ever differ, and still tells the parent
            // when they do, so the sidebar and the URL cannot drift apart.
            if (
              data.conversationId &&
              data.conversationId !== conversationIdRef.current
            ) {
              conversationIdRef.current = data.conversationId;
              onConversationStarted && onConversationStarted(data.conversationId);
            }
          } else if (event === "error") {
            // Captured rather than thrown here: throwing mid-loop would
            // abandon any event after this one that the same read() already
            // parsed into `events`, and the server can (and does) emit
            // `error` followed by `done {conversationId}` in one flush when
            // it persisted a partial turn before failing. Draining the rest
            // of the batch first means that conversationId is not lost (a
            // bug caught in review) - the error is re-thrown once the whole
            // stream has been processed, below.
            if (!streamError) {
              streamError = new Error(data.message || "Chat error");
            }
          }
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        resetIdleWatchdog();
        if (done) break;
        processEvents(decoder.decode(value, { stream: true }));
      }
      processEvents(decoder.decode() + "\n\n");
      if (streamError) throw streamError;
      // The reader finished (`done`) without a single usable `chunk` event
      // ever arriving - e.g. every block was malformed/unparsable (parseSseBuffer
      // skips those, by design, rather than throwing - see its own docstring),
      // or the connection was cut mid-block. Without this check the loop falls
      // through to flushNow("") below and the turn silently commits as an
      // empty "complete" reply: streaming flips off with nothing shown and no
      // indication anything went wrong (a bug caught in review).
      if (!assistantText) {
        const err = new Error("Chat stream ended with no content");
        err.displayMessage = GENERIC_CHAT_ERROR_MESSAGE;
        throw err;
      }
      // Commit the final text synchronously rather than leaving it to
      // whatever frame the coalescer above last scheduled - `streaming`
      // flips back off in `finally` right after this, and the bubble must
      // already show the complete answer by then, not a frame later.
      coalescerRef.current.flushNow(assistantText);
    } catch (err) {
      // Drop any frame the coalescer above has queued before deciding what
      // the bubble should show next - otherwise a still-pending frame from
      // right before the failure could fire after this and stomp the
      // abandoned/error content set below with a stale partial answer.
      coalescerRef.current.cancel();
      // No `done` event ever arrived for this turn, so the conversation
      // never got a real id - the optimistic "New chat" row ChatPage added
      // via onConversationDraft is now orphaned: unclickable (its id is a
      // sentinel, not a real one), un-renameable and un-deletable, and
      // nothing else clears it short of "New chat" or a reload (a bug
      // caught in review). Only fires when this turn was the one meant to
      // start the conversation - an established conversation (a retry/edit
      // mid-thread) already has a real id and no draft row to clean up. Run
      // BEFORE the deliberate-abort early return below, not after - a Stop
      // on a brand-new conversation's first turn is exactly the case this
      // exists for (a bug caught in review: this used to sit after the
      // return and never ran on that path).
      if (!conversationIdRef.current) {
        onConversationDraftFailed && onConversationDraftFailed();
      }
      // A deliberate close/unmount aborts the fetch - that's not a failure,
      // and must not overwrite the (now-abandoned) assistant bubble with an
      // error message nobody will see anyway. A watchdog-triggered abort
      // (streamTimedOutRef) is NOT deliberate in that sense - it needs the
      // same "genuine failure" handling below as any other error.
      if (err.name === "AbortError" && !streamTimedOutRef.current) return;
      // A genuine failure here (a thrown SSE `error` event, a mid-stream
      // network drop, a watchdog timeout) leaves the fetch's reader
      // mid-read unless explicitly aborted - without this, the upstream
      // connection (and the agent run generating it) is abandoned rather
      // than cancelled, the same "keeps streaming/billing server-side with
      // nobody reading it" problem the unmount cleanup exists to prevent (a
      // bug caught in review). Safe to call unconditionally here: this is
      // the one path where the request is not already aborted/settled.
      controller.abort();
      const displayMessage = streamTimedOutRef.current
        ? "Genie stopped responding. Try again."
        : err.displayMessage || GENERIC_CHAT_ERROR_MESSAGE;
      setMessages((prev) => {
        const copy = [...prev];
        // `assistantText` (accumulated synchronously above, per chunk), not
        // whatever the coalescer above has actually committed into
        // `copy[copy.length - 1].content` - requestAnimationFrame never
        // fires in a hidden/backgrounded tab, so a real, mostly-complete
        // answer can still read as empty here even though the model sent
        // all of it. Without this, applyStreamFailure's own "keep partial
        // content, mark it truncated" branch below never triggers in a
        // background tab - it takes the "nothing streamed yet" branch
        // instead and discards a genuine answer for a generic error message
        // (a bug caught in review).
        const current = { ...copy[copy.length - 1], content: assistantText };
        copy[copy.length - 1] = applyStreamFailure(current, displayMessage);
        return copy;
      });
    } finally {
      clearWatchdogs();
      if (readerRef.current) {
        try {
          readerRef.current.releaseLock();
        } catch (_e) {
          /* already released, or the stream already errored/closed it */
        }
        readerRef.current = null;
      }
      sendingRef.current = false;
      setStreaming(false);
      abortRef.current = null;
      const startedAt = workStartedAtRef.current;
      workStartedAtRef.current = null;
      if (startedAt && activityRef.current.length > 0) {
        const workMs = Date.now() - startedAt;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== "assistant") return prev;
          const copy = [...prev];
          copy[copy.length - 1] = { ...last, workMs };
          return copy;
        });
      }
    }
  };

  // Retries the turn at `index` by resending the preceding user message.
  // Held in a ref (mirroring conversationIdRef/coalescerRef above) rather
  // than a plain function, so the callback handed to each AssistantTurn
  // (`handleRetry` below) keeps a stable reference across renders instead of
  // defeating that component's memoization - see AssistantTurn's own
  // docstring on why a fresh closure per turn would be the exact re-render
  // cost this file works elsewhere to avoid.
  const retryTurnRef = useRef();
  retryTurnRef.current = (index, { regenerate = false } = {}) => {
    if (streaming) return;
    const userTurn = messages[index - 1];
    const assistantTurn = messages[index];
    if (userTurn && userTurn.role === "user") {
      send(userTurn.content, {
        replaceFromIndex: index - 1,
        // Retry-after-failure/truncation must resend the SAME messageId -
        // this IS that turn being retried, so the server's idempotency
        // dedupe guard must see the same key (see the comment on `send`'s
        // messageId param above). Regenerate-after-success is a genuinely
        // NEW turn asking for a different answer to an already-persisted
        // one - reusing that turn's id here would make the server's dedupe
        // guard treat it as a replay, not a new request (a bug caught in
        // review: both used to funnel through retryMessageId
        // unconditionally). `undefined` here means `send` mints a fresh id,
        // same as any other new turn.
        messageId: regenerate ? undefined : retryMessageId(assistantTurn),
      });
    }
  };
  const handleRetry = useCallback((index) => retryTurnRef.current(index), []);
  const handleRegenerate = useCallback(
    (index) => retryTurnRef.current(index, { regenerate: true }),
    [],
  );

  const editAndResendRef = useRef();
  editAndResendRef.current = (index, text) => {
    if (streaming) return;
    send(text, { replaceFromIndex: index });
  };
  const handleEditAndResend = useCallback(
    (index, text) => editAndResendRef.current(index, text),
    [],
  );

  const rateRef = useRef();
  rateRef.current = (index, rating) => {
    const target = messages[index];
    if (!target) return;
    const targetId = target.id;
    setMessages((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], feedback: rating };
      return copy;
    });
    if (!rating) return;
    saveChatFeedback({
      conversationId: conversationIdRef.current,
      messageId: target.serverMessageId,
      rating,
    }).catch(() => {
      setMessages((prev) => {
        // Re-find by the turn's stable id, not the index captured at click
        // time - Retry/Regenerate/stop can shorten or splice `messages`
        // while this request is in flight, so `index` can point at a
        // different turn (or nothing) by the time it settles (a bug
        // caught in review).
        const i = prev.findIndex((m) => m.id === targetId);
        if (i === -1) return prev;
        const copy = [...prev];
        copy[i] = { ...copy[i], feedback: null };
        return copy;
      });
      toast.error("Could not save that feedback. Try again.");
    });
  };
  const handleRate = useCallback(
    (index, rating) => rateRef.current(index, rating),
    [],
  );

  const editResultFieldRef = useRef();
  editResultFieldRef.current = async (index, key, value) => {
    const target = messages[index];
    const result = target?.result;
    if (!result) return;
    const previous = result;
    setMessages((prev) => {
      const copy = [...prev];
      if (copy[index]?.result) {
        copy[index] = {
          ...copy[index],
          result: applyFieldValue(copy[index].result, key, value),
        };
      }
      return copy;
    });
    try {
      await updateDraftField({
        kind: result.kind,
        id: result.id,
        key,
        value,
      });
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        if (copy[index]) copy[index] = { ...copy[index], result: previous };
        return copy;
      });
      throw new Error(
        err?.response?.data?.message || "That did not save. Try again.",
      );
    }
  };
  const handleEditResultField = useCallback(
    (index, key, value) => editResultFieldRef.current(index, key, value),
    [],
  );

  // CopilotResultCard's own "disabled while running" guard only works if
  // the promise it awaits settles when the request actually does, and
  // `send`'s own `streaming` check is a closure read that two synchronous
  // calls in the same tick can both pass before either commits its
  // `setStreaming(true)`. Neither guard alone stops a double-click on a
  // confirm action (publish/delete/archive/syndicate) from dispatching it
  // twice (a bug caught in review); createPendingActionGuard closes the
  // race the other two leave open, and is built once via the ref itself for
  // the same reason coalescerRef above is, not useMemo/useState.
  const pendingActionsRef = useRef(null);
  if (!pendingActionsRef.current) {
    pendingActionsRef.current = createPendingActionGuard();
  }
  const resultActionRef = useRef();
  resultActionRef.current = (index, action) => {
    const result = messages[index]?.result;
    if (!result || streaming) return undefined;
    // action.label too, not just action.kind - two actions on the same
    // result can share a kind (e.g. two differently-scoped "archive"
    // actions), and keying on kind alone collides them onto the same
    // pending-guard entry, so confirming one disables/dedupes the other (a
    // bug caught in review).
    return pendingActionsRef.current.run(
      `${index}:${action.kind}:${action.label}`,
      () => send(`${action.label} ${result.title}`),
    );
  };
  const handleResultAction = useCallback(
    (index, action) => resultActionRef.current(index, action),
    [],
  );

  // Answers a `[[choice]]` card prompt by sending the picked listing's title
  // as though the agent typed it - same "just say the name" reply a person
  // would give, and it needs no new backend contract for what "the user
  // picked card N" means. Ref-held for the same memoization reason as
  // retryTurnRef above.
  const selectCardRef = useRef();
  selectCardRef.current = (card) => {
    if (streaming) return;
    send(card.title);
  };
  const handleSelectCard = useCallback((card) => selectCardRef.current(card), []);

  // Same stable-callback-via-ref shape as retryTurnRef above, for the same
  // reason: a fresh closure per render would defeat AssistantTurn's memo.
  // Guards on `streaming` itself rather than passing a `disabled` prop down
  // - a `disabled` prop that flips on every send/stop would force every
  // rendered turn's chips to re-render too, the exact cost this file's memo
  // strategy exists to avoid.
  const sendChipRef = useRef();
  sendChipRef.current = (text) => {
    if (!streaming) send(text);
  };
  const handleChipSend = useCallback((text) => sendChipRef.current(text), []);

  // A reply can run for a while (the agent makes tool calls), so the user
  // needs a way out that isn't "navigate away". The abort path already
  // preserves whatever streamed in and resets `streaming` via the finally
  // block; the only gap is an abort that lands before the first token, which
  // would leave an empty assistant bubble on screen forever.
  const stop = () => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    // Synchronously, before touching `messages`. The abort only reaches the
    // coalescer's own cancel() in the catch block a tick later, and a frame
    // scheduled just before this writes to `copy[copy.length - 1]` - which,
    // once the empty assistant turn below is dropped, is the USER's message.
    // Without this a stop landing mid-frame overwrites what they just asked.
    coalescerRef.current.cancel();
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") return prev;
      const keptWork = last.activitySteps?.length > 0;
      const pendingText = assistantTextRef.current;
      if (!last.content && pendingText) {
        // requestAnimationFrame (which the coalescer commits through) never
        // fires in a hidden/backgrounded tab - `last.content` can still
        // read empty here even though the model already streamed real text
        // into assistantTextRef. Flush it into the turn rather than
        // dropping it below (a bug caught in review).
        const copy = [...prev];
        copy[copy.length - 1] = {
          ...last,
          content: pendingText,
          truncated: true,
          truncatedMessage: "Stopped.",
        };
        return copy;
      }
      if (!last.content && !keptWork) {
        return prev.slice(0, -1);
      }
      if (!last.content && keptWork) {
        // Work happened (a tool call ran) but no text ever streamed - kept,
        // same as the pendingText branch above, but marked truncated too so
        // the Retry affordance shows. Without this the turn rendered the
        // work panel and nothing else, forever, with no way to try again (a
        // bug caught in review).
        const copy = [...prev];
        copy[copy.length - 1] = {
          ...last,
          truncated: true,
          truncatedMessage: "Stopped.",
        };
        return copy;
      }
      return prev;
    });
  };

  const stopRef = useRef();
  stopRef.current = stop;
  useEffect(() => {
    if (!streaming) return undefined;
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      // Bound to the panel root (below), not `document` - SupportTicketModal
      // wraps this in a react-bootstrap Modal, which has its own Escape
      // handling that also lives on `document`. stopPropagation here keeps
      // Escape-stops-generation working in both the docked panel and the
      // full page, without Escape ALSO bubbling up and closing the modal
      // out from under an in-progress reply (a bug caught in review: a
      // stop was silently discarding the whole conversation, not just the
      // one turn).
      event.stopPropagation();
      stopRef.current();
    };
    const el = panelRef.current;
    if (!el) return undefined;
    el.addEventListener("keydown", onEscape);
    return () => el.removeEventListener("keydown", onEscape);
  }, [streaming]);

  const handleEscalate = () => {
    const transcript = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-10)
      .map(
        (m) =>
          `${m.role === "user" ? "Me" : "Assistant"}: ${m.content || "(empty)"}`,
      )
      .join("\n\n");
    onEscalateToTicket && onEscalateToTicket(transcript);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  // Hooks must run unconditionally on every render (the isLoggedIn() early
  // return below can't sit above them), and the focus-management effect
  // just below needs `isEmpty` - computed here, ahead of that return,
  // rather than down with `lastMessage`/`liveAnnouncement` where it doesn't
  // gate anything else.
  //
  // Not `messages.length <= 1` - a saved conversation whose history is
  // exactly one real turn (the common shape when the assistant's reply
  // failed to persist) has length 1 too, and isn't the fabricated-greeting
  // empty state this gates (a bug caught in review: layout="page" rendered
  // the "what are you working on?" prompt over a loaded conversation,
  // hiding the one message it actually had).
  const isEmpty = messages.every((m) => m.isGreeting);
  const composerRef = useRef(null);
  const wasEmptyRef = useRef(isEmpty);
  useEffect(() => {
    // The full-page empty state and the thread view render two DIFFERENT
    // textareas (one unmounts, the other mounts) - without this, sending
    // the first message drops focus to <body> and the next keystroke goes
    // nowhere (a bug caught in review).
    if (wasEmptyRef.current && !isEmpty) {
      composerRef.current?.focus();
    }
    wasEmptyRef.current = isEmpty;
  }, [isEmpty]);

  // Grow the composer with what is typed, then scroll inside it once it
  // reaches the ceiling the stylesheet sets. A one-row textarea that scrolls
  // from the second line hides what you just wrote, and every other
  // assistant grows first and scrolls second, so an agent arrives expecting
  // it. Driven off `input` so it also shrinks back after a send clears the
  // field, and it reads the ceiling from CSS rather than repeating the
  // number here where the two could drift.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    const ceiling =
      parseFloat(window.getComputedStyle(el).maxHeight) || Infinity;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, ceiling)}px`;
  }, [input, isEmpty]);

  if (!isLoggedIn()) {
    return (
      <div className="help-chat-panel help-chat-panel--locked">
        <p>Sign in to chat with the help assistant.</p>
        <div className="chat-escalate-row">
          <span>Not signed in?</span>
          <button
            type="button"
            className="escalate-link"
            onClick={handleEscalate}
          >
            Send a ticket to support instead
          </button>
        </div>
      </div>
    );
  }

  const lastMessage = messages[messages.length - 1];
  // A constant string while streaming, not the growing partial text - the
  // region only re-announces to a screen reader when its content actually
  // changes, so recomputing this from `lastMessage.content` on every
  // coalesced animation frame (as before) forced NVDA/JAWS/VoiceOver to
  // read the ENTIRE reply again on every frame, falling further behind as
  // it grew (a bug caught in review). One announcement at the start of a
  // reply, one with the finished text at the end.
  const liveAnnouncement = streaming
    ? "Genie is replying…"
    : lastMessage && lastMessage.role === "assistant"
      ? textForAnnouncement(lastMessage.content || "")
      : "";

  // Full-page layout, no conversation started yet: a centered "How can I
  // help you...?" prompt with one large input and the suggestions restyled
  // as pill chips, instead of a greeting chat bubble. Same `input`/`send`/
  // `streaming` state as the thread view below - only the markup differs.
  if (layout === "page" && isEmpty) {
    return (
      <div className="help-chat-panel help-chat-panel--page-empty">
        <div className="chat-page-empty-inner">
          <h2 className="chat-page-empty-title">
            {agentFirstName()
              ? `Hey ${agentFirstName()} - what are you working on?`
              : "Hey - what are you working on?"}
          </h2>
          <div className="chat-page-empty-input-wrap">
            {/* The hint shares a box with the textarea so it sits exactly
                where the first character will, instead of being positioned
                against the card and landing beside the caret. */}
            <span className="chat-composer-field">
              <InspirationPlaceholder hidden={Boolean(input.trim()) || streaming} />
              <textarea
                ref={composerRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                // No native placeholder: the rotating example stands in for
                // it. The label is what a screen reader gets, so the rotation
                // is never announced.
                aria-label="Ask anything, or tell me what to set up"
                rows={1}
                autoFocus
              />
            </span>
            <Button
              variant="primary"
              className={`chat-page-empty-send${streaming ? " is-stopping" : ""}`}
              onClick={() => (streaming ? stop() : send())}
              disabled={!streaming && !input.trim()}
              aria-label={streaming ? "Stop generating" : "Send message"}
            >
              {streaming ? (
                <Square size={13} fill="currentColor" strokeWidth={0} aria-hidden="true" />
              ) : (
                <ArrowUp size={18} strokeWidth={2.5} aria-hidden="true" />
              )}
            </Button>
          </div>


          <p className="chat-disclaimer chat-disclaimer--empty">
            Genie can make mistakes. Check anything important.
          </p>

          <div className="chat-escalate-row">
            <span>Need a human?</span>
            <button
              type="button"
              className="escalate-link"
              onClick={handleEscalate}
            >
              Send a ticket to support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      data-clarity-mask="true"
      className={`help-chat-panel${layout === "page" ? " help-chat-panel--page" : ""}`}
    >
      <div className="chat-thread" ref={scrollRef} onScroll={handleScroll}>
        {messages.map((m, idx) =>
          m.role === "assistant" ? (
            <AssistantTurn
              key={m.id ?? idx}
              message={m}
              isStreaming={streaming && idx === messages.length - 1}
              onRetry={handleRetry}
              onRegenerate={handleRegenerate}
              onSelectCard={handleSelectCard}
              index={idx}
              onChipSend={handleChipSend}
              openInPlace={layout === "page"}
              // The global `streaming` flag, not `isStreaming` - an
              // in-place link on ANY turn (not just the one currently
              // streaming) can unmount the panel and abort whichever turn
              // is in flight, so every turn's links need to go inert
              // together. This does cost the isStreaming-only re-render
              // optimization noted above on the toggle in and out of
              // streaming, which is an acceptable trade against silently
              // losing a reply.
              disableLinks={streaming}
              onRate={handleRate}
              onEditResultField={handleEditResultField}
              onResultAction={handleResultAction}
              canRegenerate={messages[idx - 1]?.role === "user"}
            />
          ) : (
            <UserTurn
              key={m.id ?? idx}
              message={m}
              index={idx}
              canEdit={!streaming}
              onEditAndResend={handleEditAndResend}
            />
          ),
        )}
      </div>

      {/* role="status" already implies aria-live="polite" - no need for both. */}
      <div className="chat-live-region" role="status">
        {liveAnnouncement}
      </div>



      <div className="chat-input-row">
        <textarea
          ref={composerRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            streaming
              ? "Genie is replying…"
              : "Reply, or ask for something else"
          }
          rows={1}
          disabled={streaming}
        />
        <Button
          variant="primary"
          className={`chat-send-btn${streaming ? " is-stopping" : ""}`}
          onClick={() => (streaming ? stop() : send())}
          disabled={!streaming && !input.trim()}
          aria-label={streaming ? "Stop generating" : "Send message"}
        >
          {streaming ? (
            <Square size={13} fill="currentColor" strokeWidth={0} aria-hidden="true" />
          ) : (
            <ArrowUp size={18} strokeWidth={2.5} aria-hidden="true" />
          )}
        </Button>
      </div>

      <p className="chat-disclaimer">
        Genie can make mistakes. Check anything important before
        acting on it.
      </p>

      <div className="chat-escalate-row">
        <span>Need a human?</span>
        <button
          type="button"
          className="escalate-link"
          onClick={handleEscalate}
        >
          Send a ticket to support
        </button>
      </div>
    </div>
  );
};

export default HelpChatPanel;
